import React, { ReactElement } from 'react';

import { useStatsigClient } from '@statsig/react-bindings';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

import BdsSegment from '../../../../shared/components/v3/BdsSegment/BdsSegment';
import PrimaryHero from '../../../../shared/components/v3/Heros/PrimaryHero/PrimaryHero';
import { INTEGRATIONS_ROUTE } from '../../../../shared/constants/auth';
import { SETTINGS_OPTION_MENU_NE } from '../../../../shared/constants/settings';
import { CAN_SEE_INTEGRATIONS_FG } from '../../../../shared/constants/statsig';
import {
  CLICK_ACTION,
  CONFIGURATION_SECTION,
  SECTION_CONFIGURATION,
  SETTINGS_TABS_DICT
} from '../../../../shared/constants/tagManager';
import usePermissions from '../../../../shared/hooks/usePermissions/usePermissions';
import TagManagerService from '../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_GLOBAL } from '../../../../shared/translations/commons';

import './SettingsPageLayoutNE.scss';

export interface SettingsPageLayoutNEProps {
  children: ReactElement<any, any> | ReactElement<any, any>[];
}

const SettingsPageLayoutNE = ({ children }: SettingsPageLayoutNEProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasAnyAccess } = usePermissions();
  const { client } = useStatsigClient();

  const getOptionsByRole = () => {
    const settingsOptionMenu = SETTINGS_OPTION_MENU_NE;

    return settingsOptionMenu
      .filter((option) => {
        if (option.pathName === INTEGRATIONS_ROUTE && !client?.checkGate(CAN_SEE_INTEGRATIONS_FG)) {
          return false;
        }
        return hasAnyAccess(option.permissionsRequired);
      })
      .map((option) => ({
        ...option,
        label: t(option.label)
      }));
  };

  const getLabelTexts = getOptionsByRole().map((option) => option.label);

  const getCurrentOptionIndex = () => {
    const pathName = location.pathname;
    const options = getOptionsByRole();
    for (let index = 0; index < options.length; index++) {
      const option = options[index];
      if (pathName === option.pathName) {
        return index;
      }
    }
    return 0;
  };

  const changeView = (newValue: number) => {
    const option = getOptionsByRole()[newValue];
    const gtmParams = {
      section: SECTION_CONFIGURATION,
      subsection: SETTINGS_TABS_DICT[option.id].subsection
    };
    TagManagerService.pushEvent(CLICK_ACTION, CONFIGURATION_SECTION, gtmParams);
    navigate(option.pathName);
  };

  const tabBarParams = {
    items: getLabelTexts,
    action: changeView,
    value: getCurrentOptionIndex()
  };

  return (
    <div className="settings-page-container" data-experience="new">
      <div className="settings-header">
        <PrimaryHero title={t(TRANSLATIONS_GLOBAL.header.profileMenu.settings)} color="hero-settings" />
        {tabBarParams.items.length > 1 && (
          <div className="settings-tabs">
            <div className="tabbar flex flex-row">
              <BdsSegment
                items={tabBarParams.items}
                action={tabBarParams.action}
                activeIndex={tabBarParams.value}
                fullWidth={true}
              />
            </div>
          </div>
        )}
      </div>
      <div className="settings-content">{children}</div>
    </div>
  );
};

export default SettingsPageLayoutNE;
